import { ref, watch, readonly, type Ref } from 'vue'

/**
 * Imperative timer: delays show and enforces a minimum display time on hide.
 * Used as the core of useDelayedVisibility, and directly by useLoading.
 */
export function createDelayedTimer(minDisplay: number) {
  let showTimer: ReturnType<typeof setTimeout> | undefined
  let hideTimer: ReturnType<typeof setTimeout> | undefined
  let shownAt = 0
  let _shown = false

  return {
    get shown() {
      return _shown
    },
    /** Remaining min-display time in ms (0 if not shown) */
    get remaining() {
      return _shown ? Math.max(0, minDisplay - (Date.now() - shownAt)) : 0
    },
    scheduleShow(onShow: () => void, delay = 200) {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
      if (!_shown) {
        shownAt = 0
      }
      showTimer = setTimeout(() => {
        _shown = true
        shownAt = Date.now()
        onShow()
      }, delay)
    },
    scheduleHide(onHide: () => void) {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
      if (!_shown) {
        onHide()
        return
      }
      const remaining = Math.max(0, minDisplay - (Date.now() - shownAt))
      hideTimer = setTimeout(() => {
        _shown = false
        onHide()
      }, remaining)
    },
    cancel() {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    },
  }
}

interface DelayedVisibilityOptions {
  /** ms before showing. Default: 200 */
  delay?: number
  /** ms minimum display time once shown. Default: 300 */
  minDisplay?: number
}

/**
 * Returns a reactive boolean that mirrors `source`, but with a show delay and
 * a minimum display time — so fast flips don't produce a visible flash.
 */
export function useDelayedVisibility(
  source: () => boolean,
  options: DelayedVisibilityOptions = {},
): Readonly<Ref<boolean>> {
  const { delay = 200, minDisplay = 300 } = options
  const visible = ref(false)
  const timer = createDelayedTimer(minDisplay)

  watch(
    source,
    isVisible => {
      if (isVisible) {
        timer.scheduleShow(() => {
          visible.value = true
        }, delay)
      } else {
        timer.scheduleHide(() => {
          visible.value = false
        })
      }
    },
    { immediate: true },
  )

  return readonly(visible)
}
